import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Button,
    Stack,
    Card,
    CardContent,
    Chip,
    MenuItem,
    Select,
    Avatar,
    useMediaQuery,
} from '@mui/material';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { UserContext } from '../../contexts/UserContext';
import RequestService from '../../services/RequestService';
import GameonLogo from '../../assets/images/gameup_dark.png';
import { useTheme } from '@mui/material/styles';
import RewardBadge from '../../widgets/RewardBadge';
import { ReactComponent as XpIcon } from '../../assets/icons/xp.svg';

const DashboardHome = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { user } = useContext(UserContext);
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState('Escola');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDashboard = async () => {
            try {
                const data = await RequestService.getDashboardData(
                    user.company.id
                );
                setDashboardData(data);
            } catch (err) {
                setError('Erro ao carregar dados do painel.');
            } finally {
                setLoading(false);
            }
        };

        if (user?.company?.id) {
            fetchDashboard();
        }
    }, [user]);

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    const {
        totalUsers,
        pendingPurchases,
        pendingUserContents,
        totalProductsInStore,
        users,
    } = dashboardData;

    const sortedUsers = users.sort((a, b) => b.exp - a.exp);

    return (
        <Box sx={{ padding: 2, backgroundColor: '#F5F5F5' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <img
                    src={GameonLogo}
                    alt="Logo"
                    style={{ width: '128px', height: 'auto' }}
                />
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Typography
                        sx={{
                            fontFamily: 'Poppins',
                            fontSize: '20px',
                            fontWeight: '400',
                            color: '#666666',
                            mb: 3,
                        }}
                    >
                        Resumo
                    </Typography>
                    <Grid container spacing={2}>
                        {[
                            { title: 'Jogadores Ativos', value: totalUsers },
                            {
                                title: 'Recompensas na Loja',
                                value: totalProductsInStore,
                            },
                            {
                                title: 'Pedidos Pendentes',
                                value: pendingPurchases,
                            },
                            {
                                title: 'Missões para Aprovar',
                                value: pendingUserContents,
                            },
                        ].map((kpi, index) => (
                            <Grid item xs={6} sm={6} key={index}>
                                <Box
                                    sx={{
                                        backgroundColor: '#FFFFFF',
                                        borderRadius: '12px',
                                        padding: '16px',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'Poppins',
                                            fontSize: '14px',
                                            fontWeight: '400',
                                            color: '#A3A3A3',
                                            mb: 2,
                                        }}
                                    >
                                        {kpi.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Poppins',
                                            fontSize: '30px',
                                            fontWeight: '600',
                                            color: '#1F83FF',
                                            mb: 2,
                                        }}
                                    >
                                        {kpi.value}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    <Box mt={4}>
                        <Typography
                            sx={{
                                fontFamily: 'Poppins',
                                fontSize: '20px',
                                fontWeight: '400',
                                color: '#666666',
                                mb: 3,
                            }}
                        >
                            Ações Rápidas
                        </Typography>
                        <Grid container spacing={2} mt={2}>
                            {[
                                {
                                    label: 'Adicionar Jogador',
                                    icon: <PersonAddAltOutlinedIcon />,
                                    route: '/dashboard/players/list',
                                },
                                {
                                    label: 'Aprovar Missões',
                                    icon: <ArticleOutlinedIcon />,
                                    route: '/dashboard/contents/requests',
                                },
                                {
                                    label: 'Turmas',
                                    icon: <GroupOutlinedIcon />,
                                    route: '/dashboard/players/groups',
                                },
                                {
                                    label: 'Ver Pedidos',
                                    icon: <ShoppingBagOutlinedIcon />,
                                    route: '/dashboard/store/purchases',
                                },
                            ].map((action, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <Button
                                        variant={
                                            index === 0
                                                ? 'contained'
                                                : 'outlined'
                                        }
                                        startIcon={action.icon}
                                        fullWidth
                                        onClick={() => navigate(action.route)}
                                    >
                                        {action.label}
                                    </Button>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Grid>

                {/* Right Column (Ranking) */}
                <Grid item xs={12} md={6}>
                    <Box>
                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontSize: '20px',
                                    fontWeight: '400',
                                    color: '#666666',
                                    mb: 3,
                                }}
                            >
                                Ranking
                            </Typography>
                            {/* <Select
                                value={selectedGroup}
                                onChange={(e) =>
                                    setSelectedGroup(e.target.value)
                                }
                                size="small"
                            >
                                <MenuItem value="Escola">Escola</MenuItem>
                                <MenuItem value="Turma 1">Turma 1</MenuItem>
                                <MenuItem value="Turma 2">Turma 2</MenuItem>
                            </Select> */}
                        </Grid>

                        <Box mt={2}>
                            {sortedUsers.slice(0, 8).map((player, index) => (
                                <Stack
                                    key={player.id}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={2}
                                    mt={1}
                                    sx={{
                                        padding: 1.5,
                                        backgroundColor:
                                            theme.palette.grey[100],
                                        borderBottom:
                                            index - 1 >= sortedUsers.length
                                                ? null
                                                : '1px solid #E0E0E0 ',
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={2}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily: 'Poppins',
                                                fontSize: '16px',
                                                color: '#292929',
                                                fontWeight: '500',
                                            }}
                                        >
                                            {index + 1}
                                        </Typography>
                                        <Avatar
                                            sx={{
                                                width: '36px',
                                                height: '36px',
                                            }}
                                        >
                                            {player.name.charAt(0)}
                                        </Avatar>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'Poppins',
                                                    fontSize: '14px',
                                                    color: '#292929',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                {player.name}
                                            </Typography>
                                            {player.groups.length > 0 && (
                                                <Chip
                                                    label={
                                                        player.groups[0].name
                                                    }
                                                    size="small"
                                                    sx={{
                                                        backgroundColor:
                                                            '#B9E3FF',
                                                        color: '#1969CC',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Stack>
                                    <RewardBadge
                                        icon={
                                            <XpIcon
                                                style={{
                                                    color: '#134F99',
                                                    fontSize: '24px',
                                                }}
                                            />
                                        }
                                        value={player.exp}
                                    />
                                </Stack>
                            ))}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DashboardHome;
